import { levelId } from '../models/world';

export interface LevelProgress {
  readonly stars: number;
  readonly bestScore: number;
  readonly completedAt: string;
}

export type ProgressMap = Record<string, LevelProgress>;

const STORAGE_KEY = 'graphquest.progress.v1';

export function loadProgress(): ProgressMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ProgressMap) : {};
  } catch {
    return {};
  }
}

export function saveProgress(progress: ProgressMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {}
}

export function recordResult(
  progress: ProgressMap,
  id: string,
  stars: number,
  score: number,
): ProgressMap {
  const previous = progress[id];
  if (previous && previous.stars >= stars) return progress;

  const next: ProgressMap = {
    ...progress,
    [id]: { stars, bestScore: score, completedAt: new Date().toISOString() },
  };
  saveProgress(next);
  return next;
}

export function starsFor(progress: ProgressMap, id: string): number {
  return progress[id]?.stars ?? 0;
}

export function isUnlocked(progress: ProgressMap, worldIndex: number, level: number): boolean {
  if (level <= 1) return true;
  return starsFor(progress, levelId(worldIndex, level - 1)) > 0;
}

export function totalStars(progress: ProgressMap): number {
  return Object.values(progress).reduce((sum, p) => sum + p.stars, 0);
}

export function worldStars(progress: ProgressMap, worldIndex: number): number {
  return Object.entries(progress)
    .filter(([id]) => id.startsWith(`w${worldIndex}_`))
    .reduce((sum, [, p]) => sum + p.stars, 0);
}

const SEEN_CUTSCENES_KEY = 'graphquest.cutscenes.v1';

export function seenCutscenes(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_CUTSCENES_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

export function saveSeenCutscenes(cutsceneIds: Iterable<string>): void {
  try {
    localStorage.setItem(SEEN_CUTSCENES_KEY, JSON.stringify([...new Set(cutsceneIds)]));
  } catch {}
}

export function markCutsceneSeen(cutsceneId: string): void {
  const seen = seenCutscenes();
  if (seen.has(cutsceneId)) return;

  seen.add(cutsceneId);
  saveSeenCutscenes(seen);
}

export function hasSeenCutscene(cutsceneId: string): boolean {
  return seenCutscenes().has(cutsceneId);
}
